import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

import { RouteAction } from 'actions';
import RoutingEnum from 'constants/RoutingEnum';

// -- constants
import ViewConstants from 'constants/ViewConstants';

import { CurrencyUtils, ArrayUtils, Logger } from 'PMT/utils';
import Notify from 'PMT/utils/Notify';

// -- entities
import {
  MenuPart,
  RestaurantMenu,
  MenuPartProduct,
  Product,
} from 'PMT/entities/menu';

// -- views
import MenuPartProductView from './MenuPartProductView';
import ChooseProductsModal from 'components/restaurantMenu/chooseProductsModal';
import SortableList from 'PMT/components/SortableList';

class MenuPartView extends Component {
  static propTypes = {
    menu: PropTypes.object.isRequired,

    menuPart: PropTypes.instanceOf(MenuPart).isRequired,

    restaurantMenu: PropTypes.instanceOf(RestaurantMenu).isRequired,

    menuManagedByPos: PropTypes.bool,

    currencyCode: PropTypes.string,

    /**
     * @param menuPart:MenuPart
     */
    onDelete: PropTypes.func.isRequired,

    /**
     * @param menuPart:MenuPart
     */
    onUpdate: PropTypes.func.isRequired,

    /**
     * @param menuPart:MenuPart
     */
    onUp: PropTypes.func.isRequired,

    /**
     * @param menuPart:MenuPart
     */
    onDown: PropTypes.func.isRequired,

    /**
     * no params
     */
    onEditClicked: PropTypes.func.isRequired,
  };

  state = {
    displayChooseProductsModal: false,
  };

  get menuPart(): MenuPart {
    return this.props.menuPart;
  }

  get currencyCode(): string {
    return this.props.currencyCode;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  onDelete = event => {
    event.preventDefault();
    Notify.confirmDelete(
      I18n.tr('menu.menu_edit.part.confirm_deletion'),
      result => {
        if (result) {
          this.props.onDelete(this.menuPart);
        }
      }
    );
  };

  onItemsOrderChange = items => {
    const menuPart = this.menuPart;
    menuPart.products = items;
    this.props.onUpdate(menuPart);
  };

  onAddProductClicked = event => {
    event.preventDefault();

    this.setState({
      displayChooseProductsModal: true,
    });
  };

  onEditPartCategoriesClicked = () => {
    RouteAction.redirectTo(RoutingEnum.RESTAURANT_MENU_PART_EDIT, {
      menuId: this.props.menu.id,
      menuPartId: this.menuPart.id,
    });
  };

  onChooseProductsOnProductModal = (products: Array<Product>) => {
    const menuPart = this.menuPart;

    menuPart.addProducts(products);

    this.setState({
      displayChooseProductsModal: false,
    });

    this.props.onUpdate(menuPart);
  };

  onHideChooseProductsModal = () => {
    this.setState({
      displayChooseProductsModal: false,
    });
  };

  onDeleteMenuPartProduct = (menuPartProductToDelete: MenuPartProduct) => {
    const menuPart = this.menuPart;
    menuPart.deleteProduct(menuPartProductToDelete);
    this.props.onUpdate(menuPart);
  };

  onUpdateMenuPartProduct = (menuPartProductToUpdate: MenuPartProduct) => {
    const menuPart = this.menuPart;
    menuPart.updateProduct(menuPartProductToUpdate);
    this.props.onUpdate(menuPart);
  };

  // -- renderers

  renderRow = (menuPartProduct: MenuPartProduct) => {
    const product: Product = this.restaurantMenu.getProductWithId(
      menuPartProduct.id
    );

    if (_.isNil(product)) {
      Logger.warning(
        'MenuPartView',
        `product ${menuPartProduct.id} does not exists for menu part ${
          this.menuPart.id
        }`
      );
      return null;
    }

    return (
      <MenuPartProductView
        key={product.id}
        product={product}
        menuManagedByPos={this.props.menuManagedByPos}
        menuPartProduct={menuPartProduct}
        onDelete={this.onDeleteMenuPartProduct}
        onUpdate={this.onUpdateMenuPartProduct}
        currencyCode={this.props.currencyCode}
      />
    );
  };

  renderProducts() {
    if (_.isEmpty(this.menuPart.products)) {
      return (
        <div className="simple_error_block col-xs-12 top-buffer-50">
          {I18n.tr('menu.menu_edit.part.one_product_required')}
        </div>
      );
    }

    return (
      <div className="top-buffer-20 table_container table_container_hover row">
        <SortableList
          items={this.menuPart.products}
          renderer={this.renderRow}
          onChange={this.onItemsOrderChange}
        />
      </div>
    );
  }

  renderChooseProductsModal() {
    if (!this.state.displayChooseProductsModal) {
      return null;
    }

    const selectedProducts = this.menuPart.products.map(
      (menuPartProduct: MenuPartProduct) => {
        return this.restaurantMenu.getProductWithId(menuPartProduct.id);
      }
    );

    return (
      <ChooseProductsModal
        show
        onHide={this.onHideChooseProductsModal}
        onChooseProducts={this.onChooseProductsOnProductModal}
        restaurantMenu={this.props.restaurantMenu}
        selectedProducts={selectedProducts}
      />
    );
  }

  renderName() {
    return <div className="menu_part_view__name">{this.menuPart.name}</div>;
  }

  renderArrows() {
    if (this.props.menuManagedByPos) {
      return null;
    }

    return (
      <div className="menu_part_view__arrows">
        <div
          className="menu_part_view__arrow_up"
          onClick={() => {
            this.props.onUp(this.menuPart);
          }}
        >
          <i className="fa fa-arrow-up" />
        </div>

        <div
          className="menu_part_view__arrow_down"
          onClick={() => {
            this.props.onDown(this.menuPart);
          }}
        >
          <i className="fa fa-arrow-down" />
        </div>
      </div>
    );
  }

  renderMinMax() {
    return (
      <div className="menu_part_view__min_max">
        {I18n.tr('menu.min_with_colon')} {this.menuPart.min} |{' '}
        {I18n.tr('menu.max_with_colon')} {this.menuPart.max}
      </div>
    );
  }

  renderAdditionalPrice() {
    const additionalPrice = this.menuPart.additionalPrice;
    return (
      <div className="menu_part_view__additional_price">
        {additionalPrice > 0 && (
          <span>
            (+{CurrencyUtils.formatPriceWithCurrency(additionalPrice,
              this.currencyCode)})
          </span>
        )}
        {/* {additionalPrice === 0 &&
          <span>
          (Pas de prix additionnel)
          </span>
        } */}
      </div>
    );
  }

  renderButtons() {
    return (
      <div className="menu_part_view__buttons">
        <button
          className="btn btn btn-default btn-xs"
          onClick={event => {
            event.preventDefault();
            this.onEditPartCategoriesClicked();
          }}
        >
          {I18n.tr('menu.menu_edit.part.edit_part_categories')}
        </button>
        &nbsp;&nbsp;
        {!this.props.menuManagedByPos && (
          <button className="btn btn btn-default btn-xs" onClick={this.onAddProductClicked}>
            {I18n.tr('menu.menu_edit.part.modify_products')}
          </button>
        )}
        &nbsp;&nbsp;
        <button
          className="btn btn btn-default btn-xs"
          onClick={event => {
            event.preventDefault();
            this.props.onEditClicked(this.menuPart);
          }}
        >
          {I18n.tr('menu.menu_edit.part.edit')}
        </button>
        &nbsp;&nbsp;
        {!this.props.menuManagedByPos && (
          <button className="btn btn-danger btn-xs" onClick={this.onDelete}>
            {I18n.tr('menu.menu_edit.part.delete')}
          </button>
        )}
      </div>
    );
  }

  render() {
    try {
      return (
        <div className="menu_part_view col-xs-12 top-buffer-50">
          {this.renderName()}
          {this.renderArrows()}

          <div className="clear" />

          {this.renderMinMax()}

          {this.renderAdditionalPrice()}

          {this.renderButtons()}

          {this.renderProducts()}

          {this.renderChooseProductsModal()}
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }
}

export default MenuPartView;
